// TermsText.jsx
import React from "react";
import { HashLink } from "react-router-hash-link";
import BackToTop from "../backToTop/backToTop";
import "./termsText.scss";

export default function TermsText({ onBack }) {
  const scrollWithOffset = (el, additionalOffset = 0) => {
    const headerOffset = 90; // fixed header height
    const elementPosition = el.getBoundingClientRect().top + window.scrollY;
    const offsetPosition = elementPosition - headerOffset - additionalOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const INDEX = [
    { id: "terms-membership", label: "1) Membership" },
    { id: "terms-categories", label: "2) Categories" },
    {
      id: "terms-tournaments",
      label: "3) Tournaments (Periods), Phases, and Annual Cycle",
    },
    {
      id: "terms-engagement",
      label: "4) Audience Engagement Metrics and Ratings",
    },
    { id: "terms-rankings", label: "5) Rankings & Tabs" },
    { id: "terms-earnings", label: "6) Monetary Earnings and Verification" },
    { id: "terms-voter", label: "7) Voter Earnings" },
    { id: "terms-legend", label: "8) SkitsIcon Legend" },
    { id: "terms-ip", label: "9) Disqualifications, IP, Notice-and-Takedown" },
    {
      id: "terms-moderation",
      label: "10) Reporting, Moderation, and Enforcement",
    },
    {
      id: "terms-advertising",
      label: "11) Advertising: Billing, Rewards, and Autopay",
    },
    {
      id: "terms-advertising-content-copyright-standards",
      label:
        "12) Advertising Content: Copyright, Rights, and Community Standards, and Third-Party Platforms",
    },
    { id: "terms-disputes", label: "13) Dispute Resolution (Arbitration)" },
    { id: "terms-agreement", label: "14) Agreement" },
  ];

  return (
    <div className="tv_text terms_text" id="terms-top">
      {/* Top Bar */}
      <div className="terms_topbar">
        {onBack ? (
          <button className="terms_backBtn" onClick={onBack} type="button">
            ← Back
          </button>
        ) : (
          <span />
        )}
        <h2 className="terms_title">Terms &amp; Conditions</h2>
        <span />
      </div>

      {/* Index */}
      <nav className="terms_index" aria-label="Terms index">
        <div className="terms_indexTitle">INDEX is CLICKABLE BELOW:</div>
        <div className="terms_indexGrid">
          {INDEX.map((item) => (
            <HashLink
              key={item.id}
              smooth
              replace
              to={`#${item.id}`}
              scroll={(el) => scrollWithOffset(el)}
              className="terms_indexItem"
              title={`Jump to ${item.label}`}
            >
              {item.label}
            </HashLink>
          ))}
        </div>
      </nav>
      {/* 1) Membership */}
      <section className="terms_section" id="terms-membership">
        <h3 className="terms_h3">1) Membership</h3>
        <p>
          <strong>Membership Eligibility.</strong> SkitsIcon is available to
          individuals who reside in the United States. Membership is free.
        </p>
        <p>
          <strong>Uploading Eligibility.</strong> Members who are 18 years of
          age or older may upload skits. Skits may be submitted for free during
          an open Entry Phase (also called an “Entry Window” in the app),
          subject to these Terms and any applicable Community Guidelines.
        </p>
        <p>
          <strong>Opportunity to Earn.</strong> Eligible top performers may have
          an opportunity to earn $10 per <strong>Tournament (Period)</strong>,
          per category, subject to verification and any requirements described
          in <strong>“Monetary Earnings and Verification.”</strong> Members must
          provide accurate and complete legal information during registration.
          Accounts created using false or misleading identity information may be
          suspended and are ineligible for payout.
        </p>
        <p>
          <strong>Voting Rewards.</strong> All Members may earn rewards by
          voting on skits. Each vote is counted uniquely per Member, per skit.
        </p>
        <p>
          <strong>End-of-Year Tournament.</strong> SkitsIcon may offer an
          end-of-year recognition title (the <strong>SkitsIcon Legend</strong>)
          that may include additional earnings opportunities of up to $1,000 in
          total, subject to eligibility, verification, and rules communicated
          within the app. Earnings amounts, eligibility criteria, and program
          details may be updated from time to time at SkitsIcon’s discretion and
          will be communicated within the app.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 2) Categories */}
      <section className="terms_section" id="terms-categories">
        <h3 className="terms_h3">2) Categories</h3>
        <p>
          <strong>Category Structure.</strong> SkitsIcon uses a non-overlapping
          category model. Each skit may be entered into one (1) category per{" "}
          <strong>Tournament (Period)</strong>, and categories are treated as
          distinct (for example, combined or hybrid categories like “RomCom” are
          not used).
        </p>
        <p>
          <strong>Current Categories.</strong> The eight (8) categories are:
        </p>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Comedies</h4>
          <p>
            <strong>Comedies may include (for example):</strong> Observational
            (everyday life, relatable moments), Slapstick (physical comedy),
            Satire (commentary + humor), Parody/Spoof (playful imitation of a
            style or trend), Sketch/Bit (short structured comedic scene),
            Character Comedy (a persona or exaggerated character), Dry/Deadpan
            (low-react delivery), Dark Humor (edgier themes, still subject to
            Community Standards), Farce (improbable situations + exaggeration +
            physical humor), Comedy of Errors (chaotic misunderstandings and
            mistaken actions), and Double Entendre/Misinterpreted Puns
            (wordplay, innuendo, or mistaken meaning).
          </p>
          <p>
            <strong>Additional Comedy Styles.</strong> The examples above are
            provided for inspiration and are not exhaustive. SkitsIcon may
            accept additional comedy styles and formats that are consistent with
            these Terms and Community Standards.
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Animations</h4>
          <p>
            Skits that are primarily created using non-human sources (including
            AI, puppets, cartoons, avatars, dolls, animals, emojis/emoticons, or
            video game characters/objects) must be categorized as Animations.
            This list is not exhaustive, and other non-human sources may also
            require the Animations category.
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Exaggerations</h4>
          <p>
            Exaggerations may include (for example): Emotional Overreaction (a
            small issue escalates into dramatic chaos), over-the-top reactions,
            extreme scenarios, and hyperbole-based humor.
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Relatables</h4>
          <p>
            Relatables may include everyday life events, common situations, and
            real-world reactions.
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Surreal</h4>
          <p>
            Surreal may include (for example): fantasy, sci-fi, dream-like
            sequences, bizarre POV scenarios, loop structures (ending where it
            began, repetition, disorienting effect), unexpected transitions
            (normal → chaotic or absurd shift), disjointed imagery (irrational,
            dreamlike juxtapositions), and literal metaphors (taking idioms or
            absurd ideas literally).
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Impersonations</h4>
          <p>
            Skits that feature impression work, including monologues or scenes
            performed as an impersonated character (e.g., celebrity, public
            figure, political figure, fictional character, or original persona),
            subject to Community Standards.
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Musical</h4>
          <p>
            To be categorized as Musical, the skit must include at least one
            musical element (for example: melody, instruments, rhythm, rapping,
            or singing vocals).
          </p>
        </div>
        <div className="terms_categoryBlock">
          <h4 className="terms_h4">Dramas</h4>
          <p>
            Dramas may include (for example): emotionally-driven storytelling,
            conflict scenes (arguments, betrayal, tough conversations),
            suspense/tension (build-up, uncertainty, reveals), character arcs
            (growth, redemption, downfall), monologues (confession, pleading,
            realization), and plot twists (unexpected turns that change
            meaning). The examples above are not exhaustive.
          </p>
        </div>
        <p>
          <strong>Category Accuracy.</strong> SkitsIcon may re-categorize a skit
          or take other action if a skit is placed in an incorrect category,
          including disqualification where required by the Community Standards.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 3) Tournaments */}
      <section className="terms_section" id="terms-tournaments">
        <h3 className="terms_h3">
          3) Tournaments (Periods), Phases, and Annual Cycle
        </h3>
        <p>
          <strong>Tournament (Period).</strong> A “Tournament” (also referred to
          in these Terms as a “Period”) is a time-based content cycle in which
          eligible skits are accepted, displayed, and finalized for a specific
          set of categories. The app may use show-style labels for Tournament
          phases (for example: “Entry Window,” “Showtime,” “Results,” “Wrap,”
          “Tie-Break,” “Finale,” or similar), but these Terms control.
        </p>
        <p>
          <strong>Entry Phase (Open for Uploads).</strong> During the Entry
          Phase, Members may upload a skit to enter that Tournament (Period).
          Each skit may be entered once per Tournament (Period) and in only one
          category for that Tournament (Period). SkitsIcon will announce timing
          and phase changes within the app (including via banner notices). The
          duration of the Entry Phase and the Tournament (Period) will be
          announced during the Entry Phase.
        </p>
        <p>
          <strong>Performing Phase (“Showtime”).</strong> After Entry closes,
          skits entered in that Tournament (Period) are displayed to Members and
          are eligible to receive audience engagement metrics and Member Ratings
          (which may include views, claps, and rating scores) used to determine
          top performers.
        </p>
        <p>
          <strong>Decision Phase (“Results”).</strong> After the Performing
          Phase ends, SkitsIcon processes platform metrics and Member Ratings to
          determine the top-performing skits for each category. A Tournament
          (Period) is considered ended once top performers have been determined
          for the applicable categories.
        </p>
        <p>
          <strong>Top Performer Notification and Verification Phase.</strong>{" "}
          Top performers may be notified and invited to appear on SkitsIcon’s
          YouTube channel to discuss their work and complete verification steps
          required to receive any earnings (see “Monetary Earnings and
          Verification”).
        </p>
        <p>
          <strong>Transition and Purge Phase (“Wrap + Reset”).</strong> After
          top performers are announced, SkitsIcon may remove (“purge”) skits and
          related records from the Tournament (Period), except that certain
          information may be retained (such as user account data and records
          required for safety, integrity, legal compliance, dispute resolution,
          or financial recordkeeping).
        </p>
        <p>
          <strong>Tournament (Period) Length.</strong> Tournament length may
          vary based on operational factors (including storage, moderation
          capacity, and participation levels). Tournaments (Periods) are
          generally no shorter than one (1) week and no longer than one (1)
          month, unless extended due to a tie or other integrity-related reason.
        </p>
        <p>
          <strong>Tie Extensions and Tie-Break Stage.</strong> If there is a tie
          that affects determining a top performer for one or more categories,
          SkitsIcon may extend the Tournament (Period) until a single top
          performer is determined for each affected category. Any extended time
          used to resolve a tie may be referred to as a tie-break stage.
        </p>
        <p>
          <strong>Annual Cycle and Maximum Rounds.</strong> SkitsIcon operates
          in an annual cycle that may include up to eleven (11) standard
          Tournaments (Periods), followed by a final Tournament (Period) that
          may use a different format (including fewer categories and higher
          earnings opportunities). SkitsIcon may set or adjust the maximum
          number of standard Tournaments (Periods) in a year to align with
          operational needs and the annual schedule.
        </p>
        <p>
          <strong>Final / End-of-Year Tournament Participation.</strong> The
          final Tournament (Period) may be limited to three (3) categories and
          may include participation by top performers from earlier Tournaments
          (Periods) that year. Participation in the final Tournament (Period)
          may require confirmation of availability prior to the Tournament
          starting. If an eligible candidate does not respond within the
          required timeframe, SkitsIcon may offer the opportunity to the next
          eligible candidate. Once participation is confirmed, timing may be
          scheduled and Members may be notified. Participating candidates may
          have a defined time window to produce and submit a skit.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 4) Audience Engagement */}
      <section className="terms_section" id="terms-engagement">
        <h3 className="terms_h3">4) Audience Engagement Metrics and Ratings</h3>
        <p>
          <strong>Definitions.</strong> For purposes of these Terms:
        </p>
        <ul className="terms_ul">
          <li>
            <strong>Views</strong> means a skit view recorded by SkitsIcon’s
            systems.
          </li>
          <li>
            <strong>Claps</strong> means a Member engagement action recorded by
            SkitsIcon’s systems.
          </li>
          <li>
            <strong>Member Ratings</strong> means rating scores submitted by
            Members.
          </li>
          <li>
            <strong>Top Performers</strong> means skits identified by SkitsIcon
            using platform metrics and Member Ratings.
          </li>
        </ul>
        <p>
          SkitsIcon may update how platform metrics and Member Ratings are
          calculated from time to time to improve fairness, integrity, or
          platform operations.
        </p>
        <p>
          <strong>Views.</strong> A skit must be viewed before a Member may
          vote. To vote, the Member must watch the skit to the end. Each view is
          counted uniquely per Member, per skit. Members may watch a skit
          multiple times.
        </p>
        <p>
          <strong>Ratings Weighting (“Skits $kills”).</strong> Skits $kills are
          averaged from Member Ratings using the following weighting:
        </p>
        <ul className="terms_ul">
          <li>Story Line (meaning, sequence, and events) — 40%</li>
          <li>Actors Performance — 50%</li>
          <li>Category — 5%</li>
          <li>Originality — 5%</li>
        </ul>
        <p>
          <strong>Vote Counting and Integrity.</strong> Votes are counted
          uniquely per Member account, per skit. Only one vote per account per
          skit will be counted, even if a Member attempts to vote multiple
          times. A skit must be viewed before a Member may vote (including
          viewing the skit to the end when required by the voting flow).
          SkitsIcon records voting and viewing activity through internal
          tracking systems and may use technical measures to detect, limit, or
          disregard duplicate, automated, or abusive activity.
        </p>
        <p>
          <strong>Claps.</strong> Claps help skits gain visibility across the
          platform. “Viral” means a skit may appear near the top of Members’
          feeds, not limited to a creator’s followers. Claps may be performed
          multiple times, but are counted uniquely per Member, per skit for core
          metrics.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 5) Rankings & Tabs */}
      <section className="terms_section" id="terms-rankings">
        <h3 className="terms_h3">5) Rankings &amp; Tabs</h3>
        <p>The “Top” area includes tabs such as:</p>
        <p>
          <strong>Highlighting and Visibility.</strong> These tabs may highlight
          skits that are trending, highly rated, or gaining momentum during the
          current <strong>Tournament (Period)</strong>. Tab placement and
          ordering may reflect a mix of platform metrics, Member Ratings,
          freshness/recency, and other factors used to support discovery and
          platform integrity.
        </p>
        <ul className="terms_ul">
          <li>
            <strong>Trending:</strong> highlights skits with strong recent
            momentum in the current Period. Signals may include recent
            engagement volume and velocity (for example, views, claps, and
            rating activity within a recent time window). Follower count is not
            the primary factor.
          </li>
          <li>
            <strong>Popular:</strong> means currently at the top for the{" "}
            <strong>Tournament (Period)</strong> and can include ties.
          </li>
          <li>
            <strong>Top 100:</strong> a list of up to 100 skits for the current
            Period. Ordering may be based on a selected metric (such as ratings
            or claps) or a platform-determined combined performance view.
            Ordering may change as new engagement activity is recorded.
          </li>
        </ul>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 6) Monetary Earnings */}
      <section className="terms_section" id="terms-earnings">
        <h3 className="terms_h3">6) Monetary Earnings and Verification</h3>
        <p>
          <strong>Earnings Availability.</strong> SkitsIcon may offer earnings
          opportunities to eligible top performers during certain Tournaments
          (Periods) and categories. Earnings amounts, eligibility requirements,
          and any verification steps may vary and will be communicated within
          the app.
        </p>
        <p>
          <strong>Verification Required.</strong> To receive any earnings, top
          performers must complete verification steps. Verification may include:
          providing a valid government-issued photo ID, and participating in a
          scheduled YouTube filming or appearance arranged by SkitsIcon for
          verification and creator discussion.
        </p>
        <p>
          <strong>No-Show Policy.</strong> If a top performer does not
          participate in required verification steps (including the YouTube
          appearance) within the timeframe provided, the top performer may still
          be recognized as a top performer, but will forfeit any associated
          earnings.
        </p>
        <p>
          <strong>Payment Method.</strong> Earnings are paid by business check.
          SkitsIcon will not issue payment until verification is completed.
          SkitsIcon reserves the right to withhold payout if verification
          documentation is incomplete, inconsistent, or fraudulent.
        </p>
        <p>
          <strong>Taxes.</strong> Top performers are responsible for any taxes
          associated with earnings received from SkitsIcon.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 7) Voter Earnings */}
      <section className="terms_section" id="terms-voter">
        <h3 className="terms_h3">7) Voter Earnings</h3>
        <p>
          <strong>Reward Points.</strong> Voters earn one (1) reward point per
          vote, per skit.
        </p>
        <p>
          <strong>Use of Points.</strong> Reward points may be used toward
          campaigns/advertising (where available), subject to any additional
          rules communicated within the app.
        </p>
        <p>
          <strong>Eligibility.</strong> Any Member may vote.
        </p>
        <p>
          <strong>Changes.</strong> SkitsIcon may modify how reward points are
          earned, used, or redeemed over time.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 8) Legend */}
      <section className="terms_section" id="terms-legend">
        <h3 className="terms_h3">8) SkitsIcon Legend</h3>
        <p>
          <strong>Legend Recognition.</strong> In the final Tournament (Period)
          of the year, SkitsIcon will recognize one skit as the SkitsIcon Legend
          based on platform metrics and Member Ratings (which may include views,
          claps, and rating scores) across the eligible categories. The
          SkitsIcon Legend will be showcased in the Legends Hall of Fame page
          for the year recognized.
        </p>
        <p>
          <strong>Legend Skit Eligibility.</strong> After recognition, the
          SkitsIcon Legend skit is not eligible to participate in future
          Tournaments (Periods) (including in subsequent years). This limitation
          applies to the skit, not the creator.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 9) Disqualifications & IP */}
      <section className="terms_section" id="terms-ip">
        <h3 className="terms_h3">
          9) Disqualifications, Intellectual Property, and Notice-and-Takedown
        </h3>

        <p>
          <strong>Creator-First Platform.</strong> SkitsIcon is designed to
          highlight original creators. You may upload only skits and materials
          that you created, or that you have the legal rights to use and publish
          (for example, properly licensed music/SFX, royalty-free assets,
          commissioned work with publication rights, or collaboration content
          you participated in).
        </p>
        <p>
          <strong>Category and Entry Violations.</strong> Each skit may be
          entered into only one (1) category per Tournament (Period). If a
          Member attempts to enter the same skit into more than one category
          during the same Tournament (Period), SkitsIcon may disqualify the skit
          and may restrict the Member from uploading additional skits. SkitsIcon
          may still allow the Member to vote or view skits, at SkitsIcon’s
          discretion.
        </p>
        <p>
          <strong>Original Content Requirement.</strong> Members may upload only
          skits and materials that they created or have the legal rights to use
          and publish. This includes (without limitation) music, beats, sound
          recordings, audio clips, video clips, scripts, artwork, images, and
          trademarks. Members are responsible for ensuring their uploads are
          original or properly licensed.
        </p>
        <p>
          <strong>Music + Third-Party Content.</strong> You are responsible for
          securing all necessary rights and licenses for any third-party
          materials included in your skit (including music, beats, sound
          recordings, audio clips, video clips, images, scripts, artwork, and
          trademarks). Giving credit, adding disclaimers (for example “no
          infringement intended”), or claiming “educational” or “non-profit” use
          does not automatically grant permission to use copyrighted content.
        </p>
        <p>
          <strong>Credits and Disclaimers Are Not Permissions.</strong> Giving
          credit to a rights owner, adding disclaimers (for example, “no
          infringement intended”), or claiming “educational” or “non-profit” use
          does not automatically grant permission to use copyrighted content.
          Unauthorized use — even short segments — may result in removal,
          disqualification, and/or account action. Some uses may qualify as
          “fair use” under U.S. law (for example, commentary, criticism,
          teaching, research, or news reporting), but fair use is fact-specific
          and is ultimately determined by courts.
        </p>
        <p>
          <strong>Disqualifications.</strong> SkitsIcon may disqualify a skit,
          remove or restrict content, and/or take account action if a skit or
          Member activity violates these Terms, Community Standards, or
          applicable law. Disqualification reasons may include (for example):
          copyright or intellectual property complaints, unauthorized use of
          music, scripts, video clips, artwork, images, or trademarks,
          prohibited content under Community Standards, attempts to manipulate
          platform metrics, ratings, votes, or views, submitting the same skit
          into more than one category within the same Tournament (Period), or
          other abusive, deceptive, or integrity-impacting behavior.
        </p>

        <p>
          <strong>Notice-and-Takedown Process (IP).</strong> If SkitsIcon
          receives a report or notice alleging that content infringes another
          party’s intellectual property rights, SkitsIcon may remove or disable
          access to the content, request additional information, and/or take
          other action as appropriate (including disqualification of the skit,
          suspension of the Member account, or permanent removal from the
          platform). SkitsIcon may take these actions with or without prior
          notice to the uploader.
        </p>

        <p>
          <strong>How to Submit an IP Notice.</strong> To report alleged
          infringement, the rights owner (or authorized agent) should provide
          sufficient information for SkitsIcon to investigate, which may
          include: identification of the copyrighted work or intellectual
          property allegedly infringed, identification of the content at issue
          (including enough details to locate it within the app), the reporting
          party’s name and contact information, a statement that the reporting
          party has a good-faith belief the use is not authorized, and a
          statement that the information provided is accurate and that the
          reporting party is the rights owner or authorized agent.
        </p>
        <p>
          <strong>False or Abusive Reports.</strong> Knowingly false,
          misleading, or abusive reports may violate our Community Standards and
          may result in enforcement action, including restrictions or suspension
          (including indefinite suspension in severe or repeated cases).
        </p>
        <p>
          <strong>Repeat Infringers.</strong> SkitsIcon may, in its discretion,
          restrict uploading privileges, suspend, or terminate accounts of
          Members who are alleged to be repeat infringers or who repeatedly
          upload content that violates these Terms or the Community Standards.
        </p>
        <p>
          <strong>
            Copyright Detection, Claims, and Platform Actions; No Guarantee.
          </strong>{" "}
          SkitsIcon may use automated or manual methods to detect potentially
          infringing content. Content may be removed, muted, restricted, or made
          ineligible for rankings, earnings, or tournaments if we believe it may
          violate these Terms, Community Standards, or applicable law. We do not
          guarantee that potentially infringing content will be identified
          before publication. You are solely responsible for your uploads and
          for resolving disputes with third-party rights owners. To the fullest
          extent permitted by law, SkitsIcon is not liable for third-party
          claims, losses, or enforcement actions arising from your use of
          third-party content.
        </p>
        <p>
          <strong>Third-Party Platform Rules (Including YouTube).</strong> If
          your skit is featured or reposted on SkitsIcon’s YouTube channel (or
          any other third-party platform), your content is also subject to that
          platform’s terms, copyright systems, and community guidelines. Those
          platforms may apply claims, restrictions, removals, demonetization, or
          other enforcement actions at their discretion. SkitsIcon is not
          responsible for third-party enforcement decisions, availability
          changes, or impacts to distribution or visibility.
        </p>

        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>

      {/* 10) Reporting / Moderation */}
      <section className="terms_section" id="terms-moderation">
        <h3 className="terms_h3">10) Reporting, Moderation, and Enforcement</h3>
        <p>
          <strong>Reporting a Skit.</strong> Members may report skits they
          believe violate these Terms or the Community Standards. Reporting
          flows may request the reporting Member to select a reason category
          and, in some cases, provide additional detail (for example, reports
          involving intellectual property, safety, hate, violence, adult
          content, or minors).
        </p>
        <p>
          <strong>Content Status Changes (Needs Review / Suspended).</strong>{" "}
          When a skit is reported, SkitsIcon may apply one of the following
          content statuses:
        </p>
        <ul className="terms_ul">
          <li>
            <strong>Needs Review.</strong> A skit may be marked as “needsReview”
            when it has been reported and requires review for compliance with
            these Terms or the Community Standards. A skit marked “needsReview”
            may remain visible, may have reduced visibility, or may be
            temporarily restricted while review is pending.
          </li>

          <li>
            <strong>Suspended.</strong> A skit may be marked as “suspended” when
            a report indicates a severe issue (for example, content involving
            minors, self-harm, violence, or hate) or when a skit receives a high
            volume of reports or other integrity signals. A suspended skit may
            be removed from feeds, disabled, or otherwise restricted from
            viewing or engagement.
          </li>
        </ul>
        <p>
          <strong>Enforcement Actions.</strong> Based on reports, review
          outcomes, or other integrity signals, SkitsIcon may take enforcement
          action, which may include: removing or disabling access to a skit,
          disqualifying a skit from a Tournament (Period), re-categorizing a
          skit, limiting reach/visibility, restricting a Member’s uploading
          privileges, suspending a Member account, or terminating membership.
        </p>
        <p>
          <strong>No Guarantee; Discretion.</strong> SkitsIcon does not
          guarantee that reported content will be reviewed within a specific
          timeframe or that any particular action will be taken. SkitsIcon may
          take action with or without prior notice and may consider the context,
          severity, and available information.
        </p>
        <p>
          <strong>Abuse of Reporting.</strong> Misuse of reporting tools
          (including knowingly false reports, coordinated reporting abuse, or
          attempts to manipulate enforcement outcomes) may result in action
          against the reporting account, including restrictions or suspension.
          SkitsIcon generally takes action only when content appears to violate
          Community Standards, platform integrity rules, or applicable law.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      {/* 11) Advertising */}
      <section className="terms_section" id="terms-advertising">
        <h3 className="terms_h3">
          11) Advertising Billing, Rewards, and Autopay
        </h3>

        <p>
          <strong>1) Billing Model: Clicks or Impressions.</strong> Each
          advertising campaign is billed on a single performance basis, selected
          at campaign creation:
          <strong> Clicks Campaigns</strong>: charges accrue when users click
          the ad.
          <strong> Impressions Campaigns</strong>: charges accrue when the ad is
          displayed (“impression”). The campaign’s selected billing basis
          controls how results are counted and billed.
        </p>

        <p>
          <strong>2) Campaign Budget Type: Daily or Lifetime.</strong> Campaigns
          operate under one of two budget structures:{" "}
          <strong>Daily Campaigns</strong>: subject to daily spend and/or daily
          result limits (e.g., daily clicks or impressions). Daily campaigns may
          reset eligibility after the daily reset time.
          <strong> Lifetime (One-Time Budget) Campaigns</strong>: subject to a
          total campaign budget and/or total result limits across the campaign’s
          lifetime. We may pause, throttle, or stop delivery to prevent
          exceeding your applicable limits.
        </p>

        <p>
          <strong>3) When Charges Occur.</strong> Charges are assessed only
          after measurable ad delivery results occur (clicks or impressions,
          depending on campaign type). To reduce processing frequency, we may
          charge in increments when usage reaches internal billing thresholds
          (for example, after a certain number of clicks or impressions accrue).
          Threshold counters may reset after a successful charge.
        </p>

        <p>
          <strong>4) Autopay Authorization (Per Campaign).</strong> If you
          enable Autopay for a campaign, you authorize us to automatically
          collect eligible advertising charges as they accrue, including at
          thresholds or periodic intervals, subject to your budget limits and
          available balances. If Autopay is disabled, you may be required to pay
          manually before additional delivery continues, and campaigns may pause
          when payment is due.
        </p>

        <p>
          <strong>
            5) Payment Priority: Rewards First, Then Card on File.
          </strong>{" "}
          If your account has rewards points, credits, or promotional balances
          (“Rewards”), we will apply Rewards first toward eligible advertising
          charges. If Rewards do not fully cover the charges, you authorize us
          to charge the remaining amount to your payment method on file (“Card
          on File”). This may result in: <strong>Points Payment</strong>
          (Rewards cover the full amount), <strong>Split Payment</strong>{" "}
          (Rewards + Card on File), or <strong>Card Payment</strong> (Card on
          File covers the full amount).
        </p>

        <p>
          <strong>6) Failed Payments, Retries, and Campaign Pauses.</strong> If
          a card charge fails or cannot be processed: we may retry the payment,
          we may pause or suspend the campaign, and/or we may require an updated
          payment method before delivery resumes. You are responsible for
          maintaining accurate billing details and sufficient balances.
        </p>

        <p>
          <strong>7) Reporting, Adjustments, and Final Metrics.</strong> Your
          dashboard may display metrics such as clicks, impressions, spend, and
          remaining budget. You acknowledge: metrics may be delayed due to
          processing; preliminary metrics may be adjusted to correct errors,
          duplicates, invalid activity, fraud, or system reconciliation; final
          billed totals are based on our recorded delivery and billing logs.
        </p>

        <p>
          <strong>8) Refunds and Reversals.</strong> Refunds (if any) may be
          issued to the original payment method used: card refunds may be
          recorded as refunded card amounts, and/or points refunds may be
          returned as refunded points. We may issue partial refunds where
          appropriate (for example, if only a portion of a charge is eligible
          for reversal).
        </p>

        <p>
          <strong>9) Campaign Status and Enforcement.</strong> Campaigns may be
          marked as pending, live, expired, suspended, complete, or archived. We
          may pause, suspend, or end a campaign to enforce budgets, address
          nonpayment, or enforce platform policies.
        </p>

        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      <section
        className="terms_section"
        id="terms-advertising-content-copyright-standards"
      >
        <h3 className="terms_h3">
          12) Advertising Content: Copyright, Rights, and Community Standards,
          and Third-Party Platforms
        </h3>

        <p>
          <strong>Applies to Ads and Content.</strong> This section applies to
          advertising campaigns, ad creatives, destination URLs, and any related
          materials or content you submit or use through SkitsIcon
          (collectively, <strong>“Ad Content”</strong>).
        </p>

        <p>
          <strong>Community Standards + Legal Compliance.</strong> Ad Content
          must comply with these Terms, any Community Standards or guidelines we
          publish, and applicable law. You may not submit Ad Content that is
          unlawful, deceptive, misleading, harmful, or that violates the rights
          of others.
        </p>

        <p>
          <strong>Rights and Permissions (Copyright / Trademark).</strong> You
          are responsible for ensuring you have all rights, licenses,
          permissions, and approvals necessary to use and promote your Ad
          Content. This includes (for example) music, videos, images, logos,
          trademarks, likenesses, and any third-party materials.
        </p>
        <p>
          <strong>Enforcement and Removal.</strong> We may review, reject,
          remove, pause, restrict, or disable Ad Content or campaigns at any
          time to enforce these Terms, address complaints, reduce risk, or
          protect platform integrity. We do not promise that we will identify
          all policy violations before content is reported or before it is
          delivered.
        </p>
        <p>
          <strong>Third-Party Platforms and Services.</strong> SkitsIcon may
          include links to third-party websites or services (for example,
          advertiser destination sites) and may also distribute or reference
          creator content through third-party platforms (for example, YouTube).
          Third-party platforms are independent from SkitsIcon and are governed
          by their own terms, policies, and enforcement decisions.
        </p>
        <p>
          <strong>No Control Over Third Parties.</strong> SkitsIcon does not own
          or control third-party platforms or services and is not responsible
          for their content, actions, availability, policy enforcement,
          removals, suspensions, or downtime. You are responsible for reviewing
          and complying with any third-party terms and policies that apply to
          your use of those services and any content you publish or distribute
          through them.
        </p>
        <p>
          <strong>No Endorsement.</strong> References to third-party platforms
          or services do not mean SkitsIcon endorses them, is affiliated with
          them, or is responsible for them.
        </p>
        <p>
          <strong>Service Disclaimer.</strong> To the maximum extent permitted
          by law, SkitsIcon provides the platform, ad tools, and any third-party
          links or integrations <strong>without guarantees</strong> about
          availability, uninterrupted operation, error-free performance, or that
          content will remain accessible on any third-party platform.
        </p>
        <p>
          <strong>Limitation of Liability.</strong> To the maximum extent
          permitted by law, SkitsIcon will not be liable for indirect,
          incidental, special, consequential, exemplary, or punitive damages, or
          for lost profits, lost revenue, lost data, business interruption, or
          reputational harm arising out of or related to: (a) your Ad Content,
          (b) third-party platforms or services, (c) content removals,
          restrictions, suspensions, or unavailability (whether by SkitsIcon or
          a third party), or (d) your use of destination URLs or third-party
          sites.
        </p>
        <p>
          <strong>Your Responsibility; Indemnity.</strong> To the maximum extent
          permitted by law, you agree to defend, indemnify, and hold harmless
          SkitsIcon (and its affiliates, officers, directors, employees, and
          agents) from any claims, demands, losses, liabilities, damages, costs,
          and expenses (including reasonable attorneys’ fees) arising from or
          related to: (a) your Ad Content, (b) your destination URL or
          third-party sites/services you use, (c) your violation of these Terms,
          Community Standards, or any law, or (d) your infringement or alleged
          infringement of any third-party rights (including copyright,
          trademark, privacy, or publicity rights).
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      <section className="terms_section" id="terms-disputes">
        <h3 className="terms_h3">13) Dispute Resolution (Arbitration)</h3>
        <p>
          <strong>Governing Law.</strong> These Terms are governed by the laws
          of the United States and, where applicable, the laws of the state in
          which SkitsIcon operates (excluding conflict-of-laws rules), to the
          maximum extent permitted by law.
        </p>
        <p>
          <strong>Mandatory Individual Arbitration.</strong> To the maximum
          extent permitted by law, you and SkitsIcon agree that any dispute,
          claim, or controversy arising out of or relating to SkitsIcon, these
          Terms, your account, Ad Content, campaigns, payments, or your use of
          the Services (each, a <strong>“Dispute”</strong>) will be resolved by{" "}
          <strong>binding, individual arbitration</strong> and not in court.
        </p>
        <p>
          <strong>Exceptions.</strong> This arbitration requirement does not
          prevent: (a) either party from bringing an individual claim in small
          claims court if it qualifies, or (b) either party from seeking
          temporary injunctive relief in court to protect intellectual property
          rights or prevent unauthorized use or abuse of the Services.
        </p>
        <p>
          <strong>Arbitration Provider and Rules.</strong> The arbitration will
          be administered by <strong>JAMS</strong> (or another mutually agreed
          arbitration administrator) under its applicable consumer arbitration
          rules. The arbitrator may award the same damages and relief a court
          could award on an individual basis. Judgment on the award may be
          entered in any court with jurisdiction.
        </p>
        <p>
          <strong>No Class Actions.</strong> You and SkitsIcon agree that
          Disputes will be brought only on an <strong>individual</strong> basis.
          There is no right or authority for any Dispute to be brought, heard,
          or arbitrated as a class, collective, coordinated, or representative
          action.
        </p>
        <p>
          <strong>Time Limit.</strong> To the maximum extent permitted by law,
          any Dispute must be filed within one (1) year after the claim arises,
          unless a longer period is required by law.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
      <section className="terms_section" id="terms-agreement">
        <h3 className="terms_h3">14) Agreement</h3>
        <p>
          <strong>Acceptance of Terms.</strong> By creating an account,
          accessing, or using SkitsIcon, you acknowledge that you have read and
          agree to these Terms &amp; Conditions and any referenced policies.
        </p>
        <p>
          <strong>Arbitration Acknowledgement.</strong> You specifically
          acknowledge and agree to the{" "}
          <strong>Dispute Resolution (Arbitration)</strong> section above,
          including the <strong>class action waiver</strong>, to the maximum
          extent permitted by law.
        </p>
        <p>
          <strong>Severability.</strong> If any provision of these Terms is
          found unenforceable, the remaining provisions will remain in full
          force and effect.
        </p>
        <p>
          <strong>Updates.</strong> SkitsIcon may update these Terms from time
          to time. Continued use of the platform after an update becomes
          effective constitutes acceptance of the updated Terms, except where
          prohibited by law.
        </p>
        <BackToTop scrollWithOffset={scrollWithOffset} additionalOffset={10} />
      </section>
    </div>
  );
}
